import { action, observable, toJS, makeObservable } from 'mobx';
import { runtime } from '../../../classes/runtime/Runtime';

export class MobxLocalStorage {
  @observable.shallow storage = observable.map<string, string | null | undefined>();

  nativeStorage;

  constructor(type: 'local' | 'session') {
    makeObservable(this);
    if (runtime.isServerRuntime) return;
    if (type === 'session') {
      this.nativeStorage = sessionStorage;
    } else {
      this.nativeStorage = localStorage;
    }
    this.readFromNative();
  }

  get length() {
    return this.storage.size;
  }

  getItem(key: string) {
    return this.storage.has(key) ? this.storage.get(key) : null;
  }

  setItem(key: string, value: any, options: { expire?: number } = {}) {
    this.storage.set(key, value);
    this.nativeStorage.setItem(key, value);

    if (options.expire) {
      const expireValue = (new Date().getTime() + options.expire * 1000).toString();
      this.storage.set(`expire_${key}`, expireValue);
      this.nativeStorage.setItem(`expire_${key}`, expireValue);
    }
  }

  @action
  clear() {
    this.storage.clear();
  }

  @action
  getObject(key: string): any {
    const item = this.getItem(key);
    if (!item) return undefined;
    return JSON.parse(item);
  }

  @action
  setObject(key: string, value?: any) {
    this.setItem(key, JSON.stringify(toJS(value)));
    return this;
  }

  @action
  removeItem(key: string) {
    const has = this.storage.has(key);
    this.storage.delete(key);
    this.nativeStorage.removeItem(key);
    return has;
  }

  @action.bound
  readFromNative() {
    if (runtime.isServerRuntime) return;
    if (!this.nativeStorage) return;

    const nativeKeys = Object.keys(this.nativeStorage);
    for (const key of nativeKeys) {
      this.storage.set(key, this.nativeStorage.getItem(key));
    }

    for (const key of nativeKeys) {
      if (key.startsWith('expire_')) {
        const expireTime = parseInt(key.split('expire_')[1]);
        if (expireTime < new Date().getTime()) {
          const expiredKey = this.nativeStorage.getItem(key);
          this.storage.delete(key);
          this.storage.delete(expiredKey);
          this.nativeStorage.removeItem(key);
          this.nativeStorage.removeItem(expiredKey);
        }
      }
    }
  }
}

export const localStore = new MobxLocalStorage('local');
export const sessionStore = new MobxLocalStorage('session');
